// @flow
import type { AppState, ID, Selector } from '../../core/types'
import type { Item, Media, ItemDetailModalType } from './types'
import type { Auction, ShippingOption } from '../auction/types'
import type { Bid } from '../bid/types'
import { createSelector } from 'reselect'
import { hget } from '../../modules/hash'
import { getById, getDomain } from '../../modules/entities'
import { domains, MEDIA_TYPE } from '../constants'
import { ITEM_DETAIL_MODAL } from './constants'
import { isCommCompleted } from '../../modules/communication'
import { createFetchItemResource } from './utils'

export const getItem = (id: ID): Selector<Item> => getById(domains.ITEMS, id)

export const createItemAuctionSelector = (itemId: ID): Selector<Auction> =>
  createSelector(
    getById(domains.ITEMS, itemId),
    getDomain(domains.AUCTIONS),
    (item: Item, allAuctions: { [ID]: Auction }) => allAuctions[item.auctionId]
  )

export const createItemBidsSelector = (itemId: ID): Selector<Bid[]> =>
  createSelector(
    getItem(itemId),
    getDomain(domains.BIDS),
    (item: Item, allBids: { [ID]: Bid }) =>
      (item.bids || []).map(id => allBids[id.toString()])
  )

export const createItemMediaIdsSelector = (itemId: ID): Selector<ID[]> =>
  createSelector(getById(domains.ITEMS, itemId), (item: Item) => item.media)

export const createAllItemMediaSelector = (itemId: ID): Selector<Media[]> =>
  createSelector(
    createItemMediaIdsSelector(itemId),
    getDomain(domains.MEDIA),
    (mediaIds: Media[] = [], mediaById: { [ID]: Media }) =>
      mediaIds.map(id => mediaById[id.toString()])
  )

export const createAllItemImagesSelector = (itemId: ID): Selector<any> =>
  createSelector(createAllItemMediaSelector(itemId), (itemMedia: Media[]) => {
    return itemMedia.filter(media => media && media.type === MEDIA_TYPE.IMAGE)
  })

export const createAllItemVideosSelector = (itemId: ID): Selector<any> =>
  createAllItemMediaSelector(itemId).filter(
    (media: Media) => media && media.type === MEDIA_TYPE.VIDEO
  )

export const getMedia = (id: ID, state: AppState): Selector<Media> =>
  getById(domains.MEDIA, id, state)

export const getItemModalType = (
  state: AppState
): Selector<ItemDetailModalType> => hget(ITEM_DETAIL_MODAL, 'isOpen', state)

export const isItemDetailModalOpen = (state: AppState): boolean =>
  Boolean(getItemModalType(state))

export const getIsItemLoaded = (id: ID, state: AppState): boolean => {
  const resource = createFetchItemResource(id)
  return isCommCompleted(resource, state)
}

export const getItemShippingOption = (itemId: ID): Selector<ShippingOption> =>
  createSelector(
    getItem(itemId),
    getDomain(domains.AUCTIONS),
    (item: Item, allAuctions: { [ID]: Auction }) => {
      const auction = item && allAuctions[item.auctionId]
      return auction && auction.shippingOption
    }
  )

export const getModalItemDetailId = (): Selector<number> =>
  hget(ITEM_DETAIL_MODAL, 'itemId')



// WEBPACK FOOTER //
// ./src/services/item/selectors.js